import { Button, Divider, Icon, Spinner, Stack } from '@servicetitan/design-system';
import classNames from 'classnames';
import throttle from 'lodash.throttle';
import { FC, UIEvent, useCallback, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Pagination } from './pagination';
import * as Styles from './pdf-viewer.module.less';
import { Zoom, ZoomValues } from './zoom';
// Support for text layer. Dev console may display an incorrect message that styles are not included.
import 'react-pdf/dist/Page/TextLayer.css';

export const pdfViewerJs = pdfjs;

// Maximum zoom level allowed on initial PDF load (175%)
const MAX_ZOOM_ON_INITIAL_LOAD = 1.75;

/*
 * Disallow pdf.js from evaluating PDF-embedded strings as JavaScript.
 * Defined outside the component so identity stays stable across renders (react-pdf reloads the
 * document if `options` changes).
 */
const documentOptions = { isEvalSupported: false };

export interface PdfViewerProps {
    file: string;
    loadingClassName?: string;
    scrollContainerClassName?: string;
    className?: string;
    hidePaginationButtons?: boolean;
    leftAction?: JSX.Element;
    rightAction?: JSX.Element;
}

export const PdfViewer: FC<PdfViewerProps> = props => {
    const {
        className,
        file,
        hidePaginationButtons,
        leftAction,
        loadingClassName,
        rightAction,
        scrollContainerClassName,
    } = props;
    const [numPages, setNumPages] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [zoom, setZoom] = useState<ZoomValues>(1);
    const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
    const [fitOnInitialLoad, setFitOnInitialLoad] = useState(true);

    const { current: originalWidthPerPage } = useRef(new Map<number, number>());
    const { current: originalHeightPerPage } = useRef(new Map<number, number>());

    const { current: pdfPageClassPrefix } = useRef(
        Math.floor(Math.random() * 90000) + 10000 + '_pdf_page_'
    );

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleZoomChange = (value: ZoomValues) => {
        setZoom(value);
    };

    const handlePageChange = (page: number) => {
        if (!scrollRef) {
            return;
        }
        setCurrentPage(page);
        const pageElement = scrollRef.getElementsByClassName(pdfPageClassPrefix + page)[0];
        if (pageElement) {
            scrollRef.scrollTop = (pageElement as HTMLDivElement).offsetTop - scrollRef.offsetTop;
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleScroll = useCallback(
        throttle((e: UIEvent<HTMLDivElement>) => {
            const div = e.currentTarget;
            const { top: topOffset } = div.getBoundingClientRect();
            const pages = div.getElementsByClassName('react-pdf__Page');

            let newPage = 1;

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const rect = page.getBoundingClientRect();
                const offsetFromWindowTop = rect.top - topOffset;
                if (offsetFromWindowTop > 0) {
                    break;
                }
                newPage = i + 1;
            }

            // if there is no more scroll available, set the last page
            if (
                newPage !== numPages &&
                Math.abs(div.scrollHeight - div.scrollTop - div.clientHeight) < 1
            ) {
                newPage = numPages;
            }

            setCurrentPage(newPage);
        }, 0),
        [numPages]
    );

    const handleFitToWidth = useCallback(() => {
        const viewportWidth = scrollRef?.clientWidth;
        const originalWidth = originalWidthPerPage.get(currentPage);
        if (viewportWidth && originalWidth) {
            const calculatedZoom = viewportWidth / originalWidth;
            if (fitOnInitialLoad) {
                setZoom(Math.min(calculatedZoom, MAX_ZOOM_ON_INITIAL_LOAD));
            } else {
                setZoom(calculatedZoom);
            }
        }
    }, [currentPage, fitOnInitialLoad, originalWidthPerPage, scrollRef]);

    const handleFitToHeight = () => {
        const viewportHeight = scrollRef?.clientHeight;
        const originalHeight = originalHeightPerPage.get(currentPage);
        if (viewportHeight && originalHeight) {
            const calculatedZoom = viewportHeight / originalHeight;
            if (fitOnInitialLoad) {
                setZoom(Math.min(calculatedZoom, MAX_ZOOM_ON_INITIAL_LOAD));
            } else {
                setZoom(calculatedZoom);
            }
        }
    };

    const handlePageRenderSuccess = useCallback(
        ({
            originalHeight,
            originalWidth,
            pageNumber,
        }: {
            originalWidth: number;
            originalHeight: number;
            pageNumber: number;
        }) => {
            if (originalWidth > 0) {
                originalWidthPerPage.set(pageNumber, originalWidth);
            }
            if (originalHeight > 0) {
                originalHeightPerPage.set(pageNumber, originalHeight);
            }
            if (pageNumber === 1 && fitOnInitialLoad) {
                handleFitToWidth();
                setFitOnInitialLoad(false);
            }
        },
        [fitOnInitialLoad, handleFitToWidth, originalHeightPerPage, originalWidthPerPage]
    );

    const handleFileDownload = useCallback(() => {
        const link = document.createElement('a');
        link.href = file;
        link.download = file;
        link.click();
    }, [file]);

    return (
        <Stack
            direction="column"
            className={classNames(
                'bg-neutral-30 border border-radius-bottom-2',
                className,
                Styles.pdfViewer
            )}
        >
            <Stack direction="row" className={Styles.pdfViewerToolbar} wrap="wrap" spacing={2}>
                {leftAction && (
                    <Stack.Item className={Styles.pdfViewerToolbarLeftAction}>
                        {leftAction}
                    </Stack.Item>
                )}
                <Stack.Item className={Styles.pdfViewerToolbarLeft}>
                    {!hidePaginationButtons && (
                        <Pagination
                            totalPages={numPages}
                            currentPage={currentPage}
                            onJumpToPage={handlePageChange}
                        />
                    )}
                </Stack.Item>
                <Stack.Item className={Styles.pdfViewerToolbarCenter}>
                    <Zoom
                        value={zoom}
                        onChange={handleZoomChange}
                        onFitToWidth={handleFitToWidth}
                        onFitToHeight={handleFitToHeight}
                    />
                </Stack.Item>
                <Stack.Item className={Styles.pdfViewerToolbarRight}>
                    <Button
                        onClick={handleFileDownload}
                        size="small"
                        icon={<Icon name="file_download" />}
                        data-qa="btn-download"
                        aria-label="Download"
                    />
                </Stack.Item>
                {rightAction && (
                    <Stack.Item className={Styles.pdfViewerToolbarRightAction}>
                        {rightAction}
                    </Stack.Item>
                )}
            </Stack>
            <Divider />
            <div
                ref={setScrollRef}
                className={classNames(
                    'of-y-auto flex-grow-1 flex-shrink-1 flex-basis-auto bg-neutral-30-i',
                    scrollContainerClassName
                )}
                onScroll={handleScroll}
            >
                <Document
                    file={file}
                    options={documentOptions}
                    loading={
                        <Stack
                            className={classNames('h-100', loadingClassName)}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Spinner size="medium" />
                        </Stack>
                    }
                    onLoadSuccess={onDocumentLoadSuccess}
                >
                    {/* eslint-disable-next-line prefer-spread */}
                    {Array.apply(null, Array(numPages))
                        .map((x, i) => i + 1)
                        .map(page => (
                            <Page
                                key={page}
                                className={classNames(
                                    'bg-neutral-30-i d-f justify-content-center',
                                    {
                                        [`${pdfPageClassPrefix}${page}`]: true,
                                    }
                                )}
                                scale={zoom}
                                renderAnnotationLayer={false}
                                pageNumber={page}
                                onRenderSuccess={handlePageRenderSuccess}
                            />
                        ))}
                </Document>
            </div>
        </Stack>
    );
};
